/*
    millicolor('#FF0000') :color
    millicolor({r: 10, g: 200, b: 2}) :color
    millicolor({h: 60, s: 50, l: 20}) :color
    millicolor([r, g, b]) 0...1 :color
    millicolor(0x00RRGGBB) :color

    color.toString() -> #...
    color.toHSVString() -> hsv(...)
    color.toRgbString() -> rgb(...)
    color.toArray() -> [r, g, b] 0...1
    color.toValue() -> 0x00RRGGGBB

    color.toRgb() -> { r:, g:, b: }
    color.toHsl() -> { h:, s:, l: } 
    color.getBrightness()
    color.getLuminance()

    color.desaturate(amount)
    color.saturate(amount)
    color.grayscale()
    color.lighten(amount)
    color.brighten(amount)
    color.darken(amount)
    color.spin(amount)
    color.complement()

    millicolor('#FF0000').darken().desaturate()
*/

/**
 * @classdesc A helper object to manipulate the HTML color.
 * @class
 * @hideconstructor
 * @param {number} r 0...255
 * @param {number} g 0...255
 * @param {number} b 0...255
 */
function ColorObject(r, g, b) {

    /**
     * The red component, a number from 0 to 255.
     * @type {number}
     */
    this.r = clamp255(Math.round(r || 0));
    /**
     * The blue component, a number from 0 to 255.
     * @type {number}
     */
    this.b = clamp255(Math.round(b || 0));
    /**
     * The green component, a number from 0 to 255.
     * @type {number}
     */
    this.g = clamp255(Math.round(g || 0));
}

/**
 * @returns {object} RGB object. The value of each RGB component is a number between 0 and 255.
 * @example
var color = new ColorObject(255, 0, 0); // red
color.toRgb() // { r: 255, g: 0, b: 0 }
 */
ColorObject.prototype.toRgb = function () {
    return {
        r: this.r,
        g: this.g,
        b: this.b
    };
};

/**
 * @returns {object} HSL object. The value of each HSL component is a number between 0 and 1.
 * @example
var color = new ColorObject(255, 0, 0); // red
color.toHsl() // { h: 0, s: 1, l: 0.5 }
 */
ColorObject.prototype.toHsl = function () {
    var r = bound01(this.r, 255),
        g = bound01(this.g, 255),
        b = bound01(this.b, 255),
        max = Math.max(r, g, b),
        min = Math.min(r, g, b);

    var h = 0,
        s = 0,
        l = (max + min) / 2;

    if (max !== min) {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }

        h /= 6;
    }

    return {
        h: h * 360,
        s: s * 100,
        l: l * 100
    };
};

/**
 * @returns {string} HTML hex string.
 * @example
var color = new ColorObject(255, 0, 0); // red
color.toString() // "#FF0000"
 */
ColorObject.prototype.toString = function () {
    var hex = '000000' + ((this.r << 16) + (this.g << 8) + this.b).toString(16).toUpperCase();
    return '#' + hex.substring(hex.length - 6);
};

/**
 * @returns {number[]} [r, g, b] r,g,b := [0...1]
 * @example
var color = new ColorObject(255, 0, 0); // red
color.toArray() // [1, 0, 0]
 */
ColorObject.prototype.toArray = function () {
    return [this.r / 255, this.g / 255, this.b / 255];
};

/**
 * @returns {number} 0x00RRGGBB
 * @example
var color = new ColorObject(255, 0, 0); // red
color.toValue() // 0x00FF0000
 */
ColorObject.prototype.toValue = function () {
    return (this.r << 16) | (this.g << 8) | this.b;
};

/**
 * @returns {string} HTML hsl string.
 * @example
var color = new ColorObject(255, 0, 0); // red
color.toHslString() // "hsl(0, 100, 50)"
 */
ColorObject.prototype.toHslString = function () {
    var hsl = this.toHsl();
    return 'hsl(' + Math.round(hsl.h) + ',' + Math.round(hsl.s) + ',' + Math.round(hsl.l) + ')';
};

/**
 * @returns {string} HTML rgb string.
 * @example
var color = new ColorObject(255, 0, 0); // red
color.toRgbString() // "rgb(255, 0, 0)"
 */
ColorObject.prototype.toRgbString = function () {
    return 'rgb(' + this.r + ',' + this.g + ',' + this.b + ')';
};

/**
 * Returns the inverse color.
 * 
 * @returns {ColorObject} 
 * @example
var color = new ColorObject(255, 0, 0); // red
color.inverse().toRgbString() // "rgb(0, 255, 255)"
 */
ColorObject.prototype.inverse = function () {
    var rgb = this.toRgb();
    rgb.r = 255 - rgb.r;
    rgb.g = 255 - rgb.g;
    rgb.b = 255 - rgb.b;
    return millicolor(rgb);
};

/**
 * Returns the perceived brightness of a color, from 0 to 255.
 * 
 * @returns {number} 
 * @see {@link http://www.w3.org/TR/AERT#color-contrast}
 * @example
var color = new ColorObject(255, 0, 0); // red
color.getBrightness() // 76.245
 */
ColorObject.prototype.getBrightness = function () {
    var rgb = this.toRgb();
    return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
};

/**
 * Returns the perceived luminance of a color, from 0 to 1.
 * 
 * @returns {number} 
 * @see {@link http://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef}
 * @example
var color = new ColorObject(255, 0, 0); // red
color.getLuminance() // 0.2126
 */
ColorObject.prototype.getLuminance = function () {
    var rgb = this.toRgb(),
        R01 = rgb.r / 255,
        G01 = rgb.g / 255,
        B01 = rgb.b / 255,
        R, G, B;

    if (R01 <= 0.03928) {
        R = R01 / 12.92;
    } else {
        R = Math.pow(((R01 + 0.055) / 1.055), 2.4);
    }
    if (G01 <= 0.03928) {
        G = G01 / 12.92;
    } else {
        G = Math.pow(((G01 + 0.055) / 1.055), 2.4);
    }
    if (B01 <= 0.03928) {
        B = B01 / 12.92;
    } else {
        B = Math.pow(((B01 + 0.055) / 1.055), 2.4);
    }

    return (0.2126 * R) + (0.7152 * G) + (0.0722 * B);
};

/**
 * Desaturate the color by the specified value, from 0 to 100. Setting 100 is equivalent to calling greyscale.
 * 
 * @param {number} [amount=10] 0...100
 * @returns {ColorObject}
 * @example
var color = new ColorObject(255, 0, 0); // red
color.desaturate().toRgbString() // "rgb(242, 13, 13)"
 */
ColorObject.prototype.desaturate = function (amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var hsl = this.toHsl();
    hsl.s -= amount;
    hsl.s = clamp01(hsl.s / 100) * 100;
    return millicolor(hsl);
};

/**
 * Saturate the color by the specified value, from 0 to 100.
 * 
 * @param {number} [amount=10] 0...100
 * @returns {ColorObject}
 * @example
var color = new ColorObject(242, 13, 13);
color.saturate().toRgbString() // "rgb(255, 0, 0)"
 */
ColorObject.prototype.saturate = function (amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var hsl = this.toHsl();
    hsl.s += amount;
    hsl.s = clamp01(hsl.s / 100) * 100;
    return millicolor(hsl);
};

/**
 * Completely desaturates a color into greyscale. This is equivalent to calling desaturate(100).
 * 
 * @returns {ColorObject}
 * @example
var color = new ColorObject(255, 0, 0); // red
color.grayscale().toRgbString() // "rgb(128, 128, 128)"
 */
ColorObject.prototype.grayscale = function () {
    return this.desaturate(100);
};

/**
 * Returns a lighter by the specified value, from 0 to 100 (in percent). Use the value of 100 for the white color.
 * 
 * @param {number} [amount=10] 0...100
 * @returns {ColorObject}
 * @example
var color = new ColorObject(255, 0, 0); // red
color.lighten().toRgbString() // "rgb(255, 51, 51)"
 */
ColorObject.prototype.lighten = function (amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var hsl = this.toHsl();
    hsl.l += amount;
    hsl.l = clamp01(hsl.l / 100) * 100;
    return millicolor(hsl);
};

/**
 * Returns a darker color by the specified value, from 0 to 100 (in percent). Use the value of 100 for the black color.
 * 
 * @param {number} [amount=10] 0...100
 * @returns {ColorObject}
 * @example
var color = new ColorObject(255, 0, 0); // red
color.darken().toRgbString() // "rgb(204, 0, 0)"
 */
ColorObject.prototype.darken = function (amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var hsl = this.toHsl();
    hsl.l -= amount;
    hsl.l = clamp01(hsl.l / 100) * 100;
    return millicolor(hsl);
};

/**
 * Spin the hue by the specified value, from -360 to 360. 
 * Calling with 0, 360, or -360 will do nothing (since it sets the hue back to what it was before).
 * 
 * @param {number} amount --360...360
 * @returns {ColorObject}
 * @example
var color = new ColorObject(255, 0, 0); // red
color.spin(90).toHslString() // "hsl(90, 100, 50)"
 */
ColorObject.prototype.spin = function (amount) {
    var hsl = this.toHsl();
    var hue = (hsl.h + amount) % 360;
    hsl.h = hue < 0 ? 360 + hue : hue;
    return millicolor(hsl);
};

/**
 * Returns a brighter color by the specified value, from 0 to 100 (in percent).
 * 
 * @param {number} [amount=10] 0...100
 * @returns {ColorObject}
 * @example
var color = new ColorObject(255, 0, 0); // red
color.brighten().toRgbString() // "rgb(255, 25, 25)"
 */
ColorObject.prototype.brighten = function (amount) {
    amount = (amount === 0) ? 0 : (amount || 10);
    var rgb = this.toRgb();
    rgb.r = clamp255(rgb.r - Math.round(-255 * (amount / 100)));
    rgb.g = clamp255(rgb.g - Math.round(-255 * (amount / 100)));
    rgb.b = clamp255(rgb.b - Math.round(-255 * (amount / 100)));
    return millicolor(rgb);
};

/**
 * Returns the complement color. This is equivalent to calling spin(180).
 * 
 * @returns {ColorObject}
 * @example
var color = new ColorObject(255, 0, 0); // red
color.complement().toHslString() // "hsl(180, 100, 50)"
 */
ColorObject.prototype.complement = function () {
    var hsl = this.toHsl();
    hsl.h = (hsl.h + 180) % 360;
    return millicolor(hsl);
};

function clamp01(value) {
    return Math.max(0, Math.min(1, value));
}

function clamp255(value) {
    return Math.max(0, Math.min(255, value));
}

function bound01(n, max) {
    if ((Math.abs(n - max) < 0.000001)) {
        return 1;
    }
    return (n % max) / parseFloat(max);
}

function hue2rgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
}

/**
 * Returns a new instance of ColorObject
 *
 * @private
 * @param {*} color
 * @returns {ColorObject}
 */
function millicolor(color) {
    var hex, r, g, b, a;
    if (typeof color === 'string') {
        a = color.match(/^#?([0-9A-F]{6})$/i);
        if (a) {
            hex = a[1];
            r = parseInt(hex.substr(0, 2), 16);
            g = parseInt(hex.substr(2, 2), 16);
            b = parseInt(hex.substr(4, 2), 16);
            return new ColorObject(r, g, b);
        }
        a = color.match(/^#?([0-9A-F]{3})$/i);
        if (a) {
            hex = a[1];
            r = parseInt(hex.substr(0, 1) + hex.substr(0, 1), 16);
            g = parseInt(hex.substr(1, 1) + hex.substr(1, 1), 16);
            b = parseInt(hex.substr(2, 1) + hex.substr(2, 1), 16);
            return new ColorObject(r, g, b);
        }
        throw new Error('Not a hex color: ' + color);
    }

    if (Array.isArray(color)) {
        if (color.length > 2) {
            return new ColorObject(clamp01(color[0]) * 255, clamp01(color[1]) * 255, clamp01(color[2]) * 255);
        }
        throw new Error('Can not recognize color array');
    }

    if (typeof color === 'object') {
        if (color.r !== void 0 && color.g !== void 0 && color.b !== void 0) {
            return new ColorObject(color.r, color.g, color.b);
        }
        if (color.h !== void 0 && color.s !== void 0 && color.l !== void 0) {
            var h = bound01(color.h, 360),
                s = bound01(color.s, 100),
                l = bound01(color.l, 100);

            if (s === 0) {
                r = g = b = l; // achromatic
            } else {
                var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
                var p = 2 * l - q;
                r = hue2rgb(p, q, h + 1 / 3);
                g = hue2rgb(p, q, h);
                b = hue2rgb(p, q, h - 1 / 3);
            }

            return new ColorObject(r * 255, g * 255, b * 255);
        }
        throw new Error('Can not recognize color object');
    }

    if (typeof color === 'number') {
        b = color & 0xFF;
        g = (color >> 8) & 0xFF;
        r = (color >> 16) & 0xFF;
        return new ColorObject(r, g, b);
    }

    return new ColorObject();
}

module.exports = millicolor;